/**
 * Centralized utility for managing export file paths and folder structure
 *
 * Filename base: {plotId}--{entryType}--sample{N}--{timestamp}--{collectionId}
 * For pair-bearing entry types the image, depth map, and capture metadata
 * share this base and differ only by folder + extension.
 *
 * ZIP Structure (files at the ZIP root; extracting creates a folder named after the ZIP):
 * - one_row_per_{plot,observation,object}--{taskLabel}--{timestamp}.csv
 * - README.txt
 * - export_info.json
 * - media/
 *   - images/
 *     - {base}.{ext}
 *     - annotations_coco.json
 *   - annotated_images/
 *     - {base}--annotated.jpg
 *   - depth_and_metadata/
 *     - {base}.png   <- depth map
 *     - {base}.json  <- capture metadata
 *   - audio/
 *     - {base}.{ext}
 */

/**
 * Export folder paths within the ZIP
 */
export const EXPORT_FOLDERS = {
  MEDIA_ROOT: "media",
  IMAGES: "media/images",
  ANNOTATED_IMAGES: "media/annotated_images",
  DEPTH_AND_METADATA: "media/depth_and_metadata",
  AUDIO: "media/audio",
} as const;

/**
 * Get the ZIP path for an annotated image file
 */
export function getAnnotatedImageZipPath(filename: string): string {
  return `${EXPORT_FOLDERS.ANNOTATED_IMAGES}/${filename}`;
}

/**
 * Get the ZIP path for the COCO annotations file
 */
export function getCocoAnnotationsZipPath(): string {
  return `${EXPORT_FOLDERS.IMAGES}/annotations_coco.json`;
}

/**
 * Get the CSV reference path for an image file
 * (relative path as it appears in the CSV)
 */
export function getImageCsvPath(filename: string): string {
  return `${EXPORT_FOLDERS.IMAGES}/${filename}`;
}

/**
 * Get the CSV reference path for a depth file
 */
export function getDepthCsvPath(filename: string): string {
  return `${EXPORT_FOLDERS.DEPTH_AND_METADATA}/${filename}`;
}

/**
 * Get the ZIP path for an audio file
 */
export function getAudioZipPath(filename: string): string {
  return `${EXPORT_FOLDERS.AUDIO}/${filename}`;
}

/**
 * Get the CSV reference path for an audio file
 */
export function getAudioCsvPath(filename: string): string {
  return `${EXPORT_FOLDERS.AUDIO}/${filename}`;
}

export interface MediaDownloadOptions {
  includeAnnotatedImages: boolean;
  includeOriginalImages: boolean;
  includeDepthAndMetadata: boolean;
  includeAudio: boolean;
}

export type ExportMediaType = "image" | "depth" | "metadata" | "audio";

const MEDIA_FOLDER_BY_TYPE: Record<ExportMediaType, string> = {
  image: EXPORT_FOLDERS.IMAGES,
  depth: EXPORT_FOLDERS.DEPTH_AND_METADATA,
  metadata: EXPORT_FOLDERS.DEPTH_AND_METADATA,
  audio: EXPORT_FOLDERS.AUDIO,
};

/**
 * Get the ZIP path for a media file of the given type
 */
export function getMediaZipPath(type: ExportMediaType, filename: string): string {
  return `${MEDIA_FOLDER_BY_TYPE[type]}/${filename}`;
}

/**
 * Get the ZIP path for the README file
 */
export function getReadmeZipPath(): string {
  return "README.txt";
}

/**
 * Get the ZIP path for the export identity file (Firestore IDs and full names)
 */
export function getExportInfoZipPath(): string {
  return "export_info.json";
}

export function getCollectionCapturesZipPath(): string {
  return "collection_captures.json";
}

/**
 * Longest allowed `{zip filename}/{path inside the zip}` for any file in an export.
 *
 * Windows caps a full path at 259 characters (MAX_PATH = 260 with the terminating NUL), and
 * Explorer's built-in zip support ignores the long-path setting. Opening a file from inside a
 * zip copies it to `%TEMP%\Temp1_{zip filename}\{path}`, and "Extract all" writes to
 * `{folder}\{zip stem}\{path}`, so the zip name always sits in front of the inner path.
 * 80 characters are left for the user's folder (e.g. `C:\Users\first.lastname\OneDrive -
 * Company\Downloads\`) and 4 for the " (1)" a browser adds to a repeat download.
 */
export const EXPORT_PATH_BUDGET = 259 - 80 - 4;

/** Ordinary exports keep at least this many task-label characters. */
const MIN_TASK_LABEL_CHARS = 12;

export interface ExportFileNames {
  /** The task name as it appears in file names (may be shortened) */
  taskLabel: string;
  taskLabelShortened: boolean;
  /** Local time as YYYYMMDDHHMMSS, in the ZIP and CSV names */
  timestamp: string;
  zipFilename: string;
  csvFilenames: { plot: string; observation: string; object: string };
  /** The longest `{zip filename}/{path inside the zip}` in the export */
  longestPath: string;
  withinBudget: boolean;
}

/**
 * Name the ZIP and CSVs so every path in the export fits EXPORT_PATH_BUDGET.
 *
 * The task name keeps today's form (every character other than a letter or digit becomes "_")
 * and is shortened only as far as needed. Ordinary exports keep MIN_TASK_LABEL_CHARS;
 * collection packages opt into a shorter label when UUID media names need the room. If even
 * that does not fit, `withinBudget` is false.
 *
 * @param contentPaths - Every path inside the zip that doesn't contain the task name (media,
 *   annotated images, audio, COCO). README.txt and export_info.json are added here.
 */
export function buildExportFileNames({
  taskName,
  timestamp,
  contentPaths,
  allowShortTaskLabel = false,
}: {
  taskName: string;
  timestamp: string;
  contentPaths: readonly string[];
  /** UUID collection packages can need a shorter container name; media names remain unchanged. */
  allowShortTaskLabel?: boolean;
}): ExportFileNames {
  const fullLabel = taskName.replace(/[^a-zA-Z0-9]/g, "_") || "task";
  const longer = (a: string, b: string) => (b.length > a.length ? b : a);
  const longestContentPath = [
    ...contentPaths, getReadmeZipPath(), getExportInfoZipPath(), getCollectionCapturesZipPath(),
  ].reduce(longer, "");

  const namesFor = (label: string) => {
    const zipFilename = `${label}--${timestamp}.zip`;
    const csvFilenames = {
      plot: `one_row_per_plot--${label}--${timestamp}.csv`,
      observation: `one_row_per_observation--${label}--${timestamp}.csv`,
      object: `one_row_per_object--${label}--${timestamp}.csv`,
    };
    const longestInnerPath = [
      longestContentPath,
      csvFilenames.plot,
      csvFilenames.observation,
      csvFilenames.object,
    ].reduce(longer);
    const longestPath = `${zipFilename}/${longestInnerPath}`;
    return { zipFilename, csvFilenames, longestPath, withinBudget: longestPath.length <= EXPORT_PATH_BUDGET };
  };

  let taskLabel = fullLabel;
  let names = namesFor(taskLabel);
  const minimumLabelChars = allowShortTaskLabel ? 1 : MIN_TASK_LABEL_CHARS;
  for (let length = fullLabel.length - 1; !names.withinBudget && length >= minimumLabelChars; length--) {
    const cut = fullLabel.slice(0, length);
    taskLabel = cut.replace(/_+$/, "") || cut;
    names = namesFor(taskLabel);
  }

  return { taskLabel, taskLabelShortened: taskLabel !== fullLabel, timestamp, ...names };
}
